/**
 * Custom hook for creating citation component renderers
 *
 * Creates react-markdown components for rendering citation badges.
 * Extracted from ChatPanel.tsx to reduce complexity.
 */

import { useCallback } from 'react';
import type { Components } from 'react-markdown';
import CitationBadge from '../../CitationBadge';
import type { ChatMessage } from '../../ChatPanel';

interface CitationData {
  document_name: string;
  page: number;
  paragraph?: number;
  text: string;
  chunk_id?: string;
  full_text?: string;
  document_type?: string;
  company_name?: string;
  document_period?: string;
  document_id?: string;
}

export interface UseCitationComponentsProps {
  onOpenPDF: (citationData: CitationData) => void;
  onOpenChunk: (citationData: CitationData) => void;
}

export interface UseCitationComponentsReturn {
  createCitationComponents: (message: ChatMessage) => Components;
}

/**
 * Hook to create citation component renderers for react-markdown
 */
export function useCitationComponents({
  onOpenPDF,
  onOpenChunk,
}: UseCitationComponentsProps): UseCitationComponentsReturn {
  const createCitationComponents = useCallback(
    (message: ChatMessage): Components =>
      // Cast to any to allow custom AST node type 'citation' created by remarkCitations plugin.
      // react-markdown's Components type only knows about standard HTML elements, but remark
      // plugins can introduce custom node types that react-markdown will route to a matching
      // component key if registered here.
      ({
        citation: ({ node, children }: any) => {
          const citationNumber =
            node?.data?.hProperties?.['data-citation-number'] ??
            node?.properties?.['data-citation-number'] ??
            node?.value; // remarkCitations.ts sets value = citationNumber
          if (!citationNumber) return null;

          const citation = message.citations?.[citationNumber as string];
          if (citation) {
            return (
              <CitationBadge
                citationNumber={String(citationNumber)}
                citationData={citation}
                onOpenDocument={() => onOpenPDF(citation)}
                onOpenChunk={() => onOpenChunk(citation)}
              />
            );
          }
          return (
            <CitationBadge
              citationNumber={String(citationNumber)}
              citationData={{ document_name: 'Cargando...', text: '', page: 0 }}
              onOpenDocument={() => {}}
            />
          );
        },
        span: ({ node, children, ...props }: any) => {
          const citationNumber = props['data-citation-number'] ?? props['dataCitationNumber'];
          if (citationNumber) {
            if (message.isStreaming) {
              const citation = message.citations?.[citationNumber as string];
              if (citation) {
                return (
                  <CitationBadge
                    citationNumber={String(citationNumber)}
                    citationData={citation}
                    onOpenDocument={() => onOpenPDF(citation)}
                    onOpenChunk={() => onOpenChunk(citation)}
                  />
                );
              }
              return (
                <CitationBadge
                  citationNumber={String(citationNumber)}
                  citationData={{ document_name: 'Cargando...', text: '', page: 0 }}
                  onOpenDocument={() => {}}
                />
              );
            }
            if (message.citations) {
              const citation = message.citations[citationNumber as string];
              if (citation) {
                return (
                  <CitationBadge
                    citationNumber={String(citationNumber)}
                    citationData={citation}
                    onOpenDocument={() => onOpenPDF(citation)}
                    onOpenChunk={() => onOpenChunk(citation)}
                  />
                );
              }
            }
          }
          return <span {...props}>{children}</span>;
        },
      }) as any,
    [onOpenPDF, onOpenChunk]
  );

  return { createCitationComponents };
}

export default useCitationComponents;
